
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import React from 'react';
import flush from 'just-flush';
import compact from 'just-compact';
import { GetStaticPaths, GetStaticProps } from 'next';
import { useQuery } from 'urql';
import { clientFactory, useFragment } from '@/gql';
import { acfFirst, findInArrayWaterfall, normalizeLocale, withPrepass } from '@/utils';
import { FindAllPageSummariesQueryDocument, FindPageDetailByIdQueryDocument, FindPageSummariesForSlugOrIdQueryDocument, } from '@/gql/client/graphql';
import { LanguageFieldsFragment, PageFieldsFragment, PageSummaryFieldsFragment, } from '@/gql/documents/fragments';
import { i18n } from '@/config';
import { ContentRenderer, Layout, SeoHead } from '@/components';
type UrlParams = {
    slugOrId: string[];
};
type PageProps = {
    databaseId: number;
    preview?: boolean;
};
export default function Page({ databaseId, preview }: PageProps) {
    const [{ data }] = useQuery({
        query: FindPageDetailByIdQueryDocument,
        variables: { id: String(databaseId), asPreview: Boolean(preview) }
    });
    const fields = useFragment(PageFieldsFragment, data?.page);
    const summaryFields = useFragment(PageSummaryFieldsFragment, fields);
    if (!data || !fields || !summaryFields)
        return null;
    return (<Layout preview={preview} theme={acfFirst(fields.pageCustomFields?.theme) === 'dark' ? 'dark' : 'light'} enableLines={fields.pageCustomFields?.enableLines ?? false} useFixedNavbar={fields.pageCustomFields?.enableOverlayNavbar ?? false} hideFooterDivider={fields.pageCustomFields?.hideFooterDivider ?? false} hideSidetext={fields.pageCustomFields?.hideSidetext ?? false} sidetext={fields.pageCustomFields?.sidetext?.text ? {
            text: fields.pageCustomFields.sidetext.text,
            placement: (acfFirst(fields.pageCustomFields.sidetext.placement) ?? 'left') as ('left' | 'right')
        } : undefined}>
      <SeoHead seo={fields.seo ?? undefined}/>
      <ContentRenderer enableLines={fields.pageCustomFields?.enableLines ?? false} html={fields.content ?? ''}/>
    </Layout>);
}
export const getStaticPaths: GetStaticPaths<UrlParams> = async () => {
    const client = clientFactory();
    const allPages = await client.query(FindAllPageSummariesQueryDocument, {}).toPromise();
    const nodes = allPages.data?.pages?.nodes ?? [];
    const paths = flush(nodes.map((node) => {
        const fields = useFragment(PageSummaryFieldsFragment, node);
        const language = useFragment(LanguageFieldsFragment, fields?.language);
        if (!fields?.slug)
            return null;
        return {
            locale: language?.code ? normalizeLocale(language?.code) : i18n.defaultLocale,
            params: { slugOrId: compact(fields.slug.split('/')) }
        };
    }));
    return {
        paths,
        fallback: 'blocking'
    };
};
const getStaticProps: GetStaticProps<PageProps, UrlParams> = async ({ preview, params, locale }) => {
    if (!params?.slugOrId.length) {
        return {
            notFound: true
        };
    }
    return withPrepass({
        params,
        preview,
        locale,
        propsFactory: async ({ urqlClient }) => {
            const queryParams = params.slugOrId.length > 1 || Number.isNaN(Number(params.slugOrId[0]))
                ? { slug: params.slugOrId.join('/') }
                : { databaseId: Number(params.slugOrId[0]) };
            const { data: pagesQuery, error } = await urqlClient.query(FindPageSummariesForSlugOrIdQueryDocument, queryParams).toPromise();
            if (error) {
                throw error;
            }
            const pages = useFragment(PageSummaryFieldsFragment, flush(pagesQuery?.pages?.nodes ?? []));
            const page = findInArrayWaterfall(pages, [locale ?? i18n.defaultLocale, i18n.defaultLocale], (entry, param) => {
                const language = useFragment(LanguageFieldsFragment, entry.language);
                return Boolean(language?.code && locale === normalizeLocale(param));
            });
            if (!page) {
                return {
                    notFound: true
                };
            }
            return {
                preview: !!preview,
                databaseId: page.databaseId
            };
        },
        Component: Page
    });
};

    async function __Next_Translate__getStaticProps__1a1104ac944__(ctx) {
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/[...slugOrId]',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a1104ac944__ as getStaticProps }
  